import * as moment from "moment";
import {JobAgentChangeEventDataApiDto, JobEventApiDto} from "../../../api/model/rest";
import {getUser} from "../../../store/view/user";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import {option, Option} from "ts-option";
import JobEvent, {JobEventTypeEnum} from "./JobEvent";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";

/**
 * Job Agent changed event.
 */
export default class JobAgentChangedEvent extends JobEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} jobId job id.
	 * @param {JobEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber job reference number.
	 * @param {JobAgentChangedEventData} data job Agent change event data.
	 */
	constructor(public readonly id: string,
		public readonly jobId: string,
		public readonly type: JobEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string,
		public readonly data: JobAgentChangedEventData) {
		super(id, jobId, type, entity, creationDate, referenceNumber);
	}

	public static fromJson(json: JobEventApiDto): JobAgentChangedEvent {
		const data = JobAgentChangedEventData.fromJson(json.data as JobAgentChangeEventDataApiDto);
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new JobAgentChangedEvent(json.id, json.jobId, JobEventTypeEnum.AgentAssigneeChanged, entity,
			moment(json.createdAt), json.referenceNumber, data);
	}

	getIcon() {
		return Icon.User;
	}

	getActivityMessage(): string {
		return `Agent changed from ${this.getPreviousDisplayName()} to ${this.getCurrentDisplayName()}`
	}

	/**
	 * @return {string} display name of previous Agent.
	 */
	getPreviousDisplayName(): string {
		return this.data.previousAgentId
			.map(id => getUser(id)
				.map(u => u.getDisplayName())
				.getOrElse("Unassigned")
			).getOrElse("Unassigned");
	}

	/**
	 * @return {string} display name of current Agent.
	 */
	getCurrentDisplayName(): string {
		return this.data.currentAgentId
			.map(id => getUser(id)
				.map(u => u.getDisplayName())
				.getOrElse("Unassigned")
			).getOrElse("Unassigned");
	}
}

/**
 * Data specific for Agent change event.
 */
export class JobAgentChangedEventData {
	/**
	 * @param {Option<string>} previousAgentId id of the user being previously assigned as Agent.
	 * @param {Option<string>} currentAgentId id of the user being currently assigned as Agent.
	 */
	constructor(public readonly previousAgentId: Option<string>, public readonly currentAgentId: Option<string>) {}

	public static fromJson(json: JobAgentChangeEventDataApiDto): JobAgentChangedEventData {
		return new JobAgentChangedEventData(option(json.previous), option(json.current));
	}
}